import { useTranslations } from "next-intl";
import { type Resolver, useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { type CountryCode } from "@/config/country-config";
import { useCountryConfig } from "@/config/country-context";
import { getCountryRules } from "@/config/country-rules";
import {
	buildFinnishIban,
	findFinnishAccountIssue,
} from "@/features/customer-information/utils/finnish-account";
import { shouldShowLoanPurposeSelectFromContext } from "@/features/customer-information/utils/loan-purpose";
import {
	isValidNumberOfCars,
	shouldAskNumberOfCars,
} from "@/features/customer-information/utils/number-of-cars";
import {
	isManuallyEnteredSwedishAccount,
	isValidSwedishAccountNumber,
	isValidSwedishClearingNumber,
	isValidSwedishFullAccountNumber,
	resolveSwedishAccountNumberForSubmit,
	swedishAccountDigits,
} from "@/features/customer-information/utils/swedish-account-fields";

export interface MyMoneyCustomerForm {
	accountNumber: string;
	clearingNumber: string;
	/**
	 * SE: `accountNumber` already holds a whole clearing + account number, so it
	 * is validated on its total length and no clearing field is shown. Kept in
	 * the form values (not captured from a prop) so a prefill that arrives after
	 * the first render cannot be validated with the split-field rules.
	 */
	isWholeAccountNumber: boolean;
	/**
	 * The prefilled account number as it was resolved, so an untouched prefill
	 * can be told apart from one the customer typed over. Never submitted.
	 */
	prefilledAccountNumber: string;
	isPEP: boolean;
	citizenship: string[];
	autogiroConsent: boolean;
	payoutToMyMoneyConsent: boolean;
	loanPurpose: string;
	dueDate: string;
	/** Asked when FLOW has no car count; `null` until answered. */
	numberOfCars: number | null;
}

interface UseMyMoneyCustomerFormArgs {
	isCoapplicant?: boolean;
	context?: { purpose?: string; numberOfCarsReceived?: boolean | null };
	trustedAccountNumbers?: string[];
}

export const useMyMoneyCustomerForm = ({
	isCoapplicant,
	context,
	trustedAccountNumbers = [],
}: UseMyMoneyCustomerFormArgs) => {
	const t = useTranslations("forms.errors");
	const tPage = useTranslations("pages.CustomerInformationPage");
	const { rules } = useCountryConfig();
	const showLoanPurposeSelect = shouldShowLoanPurposeSelectFromContext({
		...context,
		isCoapplicant,
	});
	const askNumberOfCars = shouldAskNumberOfCars(context ?? {});
	const hasMultipleTrustedAccounts =
		new Set(trustedAccountNumbers.map(swedishAccountDigits)).size > 1;

	const booleanMustBeTrue = (fieldName: string) =>
		z.boolean().refine((value) => value === true, {
			message: t("general.required"),
			path: [fieldName],
		});

	const sharedSchema = z.object({
		citizenship: z.array(z.string()).min(1, t("multiselect.min1Option")),
		isPEP: z.boolean({
			error: t("general.required"),
		}),
		payoutToMyMoneyConsent: booleanMustBeTrue("payoutToMyMoneyConsent"),
		accountNumber: z.string(),
		clearingNumber: z.string(),
		isWholeAccountNumber: z.boolean(),
		prefilledAccountNumber: z.string(),
		loanPurpose: z.string(),
		dueDate: z.string(),
		autogiroConsent: z.boolean(),
		numberOfCars: z.number().nullable(),
	});

	const formSchema = sharedSchema.superRefine((data, ctx) => {
		if (showLoanPurposeSelect && !data.loanPurpose) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: tPage("errors.mandatoryField"),
				path: ["loanPurpose"],
			});
		}

		if (askNumberOfCars && !isValidNumberOfCars(data.numberOfCars)) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message:
					data.numberOfCars === null
						? tPage("errors.mandatoryField")
						: tPage("numberOfCars.invalid"),
				path: ["numberOfCars"],
			});
		}

		if (isCoapplicant) return;

		if (rules.payoutAccount === "swedish") {
			// The select and the prefilled input both hold whole numbers; only
			// manual entry is split into clearing and account fields.
			const validatesWholeAccountNumber =
				data.isWholeAccountNumber || hasMultipleTrustedAccounts;

			if (!validatesWholeAccountNumber) {
				if (!data.clearingNumber.trim()) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: tPage("errors.mandatoryField"),
						path: ["clearingNumber"],
					});
				} else if (!isValidSwedishClearingNumber(data.clearingNumber)) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: tPage("errors.invalidClearingNumber"),
						path: ["clearingNumber"],
					});
				}
			}

			const isManualEntry = isManuallyEnteredSwedishAccount({
				accountNumber: data.accountNumber,
				prefilledAccountNumber: data.prefilledAccountNumber,
				isWholeAccountNumber: data.isWholeAccountNumber,
				hasMultipleTrustedAccounts,
			});

			if (!data.accountNumber.trim()) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: tPage("errors.mandatoryField"),
					path: ["accountNumber"],
				});
			} else if (isManualEntry) {
				const hasValidAccountFormat = validatesWholeAccountNumber
					? isValidSwedishFullAccountNumber(data.accountNumber)
					: isValidSwedishAccountNumber(data.accountNumber);

				if (!hasValidAccountFormat) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: tPage("errors.invalidAccountNumber"),
						path: ["accountNumber"],
					});
				}
			}
		}

		const finnishAccountIssue =
			rules.payoutAccount === "finnish-iban"
				? findFinnishAccountIssue(data)
				: undefined;

		if (finnishAccountIssue) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message:
					finnishAccountIssue === "mandatory"
						? tPage("errors.mandatoryField")
						: tPage("errors.invalidAccountNumber"),
				path: ["accountNumber"],
			});
		}

		if (rules.dueDateChoice && !data.dueDate) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: tPage("errors.mandatoryField"),
				path: ["dueDate"],
			});
		}

		if (!data.autogiroConsent) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: tPage("errors.mandatoryField"),
				path: ["autogiroConsent"],
			});
		}
	});

	return {
		showLoanPurposeSelect,
		askNumberOfCars,
		useForm: useForm<MyMoneyCustomerForm, unknown, MyMoneyCustomerForm>({
			resolver: zodResolver(formSchema) as unknown as Resolver<
				MyMoneyCustomerForm,
				unknown,
				MyMoneyCustomerForm
			>,
			mode: "onTouched",
			defaultValues: {
				isPEP: undefined,
				citizenship: [],
				payoutToMyMoneyConsent: undefined,
				accountNumber: "",
				clearingNumber: "",
				isWholeAccountNumber: false,
				prefilledAccountNumber: "",
				autogiroConsent: undefined,
				loanPurpose: "",
				dueDate: "",
				numberOfCars: null,
			},
		}),
		schema: formSchema,
	};
};

export const resolveMyMoneyAccountNumberForSubmit = ({
	values,
	country,
	hasMultipleTrustedAccounts,
}: {
	values: MyMoneyCustomerForm;
	country: CountryCode;
	hasMultipleTrustedAccounts: boolean;
}): string => {
	if (getCountryRules(country).payoutAccount === "finnish-iban") {
		return buildFinnishIban(values.accountNumber);
	}

	return resolveSwedishAccountNumberForSubmit(
		values,
		values.isWholeAccountNumber || hasMultipleTrustedAccounts
	);
};
